/* Softener collection v3. Prices and variant identities come from the section's Shopify data.
   Cart submission stays with product-form.js; this component only changes its selected variant. */
(() => {
  'use strict';
  if (window.CQSoftenersV3) {
    window.CQSoftenersV3.init(document);
    return;
  }

  const instances = new WeakMap();
  const rootSelector = '[data-softeners-v3]';
  const defaults = {
    addToCart: 'Add to cart', soldOut: 'Sold out', unavailable: 'Unavailable',
    question: 'Question {number} of {total}', ready: 'Your pick is ready', answersHeading: 'Your answers',
    startOver: 'Start over', timeNote: 'Takes about 20 seconds.',
    pickKicker: 'A starting point for your home', initialKicker: 'A good place to start',
    initialWhy: 'Answer the three questions to compare the options for your water.',
    seeSystems: 'See the systems', seeSoftener: 'See the softener', seeConditioner: 'See the conditioner',
    specialist: 'Talk to a specialist', fullCalculator: 'Use the full size calculator',
    softenerLimit: 'A salt softener adds sodium and needs salt refills, a drain and power. Confirm sizing and any pretreatment with a water test.',
    conditionerLimit: 'A conditioner controls scale without removing hardness. Water feels the same and mineral spots can remain. Confirm your water is suitable.',
    reasonHigh: 'At 25 gpg or more, we recommend a salt softener for a home. If avoiding salt is essential, ask a specialist to confirm an appropriate salt-free configuration.',
    reasonNoSaltWell: 'You prefer no salt. A conditioner may be an option, but private well water needs testing and specialist confirmation before choosing it.',
    reasonNoSalt: 'You prefer no salt. A conditioner can help control scale while keeping the minerals in your water; confirm your measured hardness and water quality first.',
    reasonWell: 'A salt softener is our usual starting point for hard private well water. Test hardness, iron and manganese before confirming the treatment and size.',
    reasonMid: 'For measured hardness of 7 to 24 gpg, a salt softener removes the hardness minerals and gives the soft-water rinse.',
    reasonLow: 'Under 7 gpg, treatment depends on the measured level and the problem you want to solve. Naturally soft water may need neither system; a conditioner is an option when scale is the concern.',
    reasonUnknown: 'On city water, salt-free scale control is a starting point to discuss. Measure hardness before choosing; naturally soft water may need neither system.',
    softBand: 'Soft', moderateBand: 'Moderately hard', hardBand: 'Hard', veryHardBand: 'Very hard',
    compareValue: '{percent}% softened illustration',
    sizeName: '{size} cu ft, {grains} grains', shopSize: 'Shop the {size} cu ft softener',
    largerSize: 'A larger system, sized with a specialist', checkUse: 'Confirm your household demand',
    checkHardness: 'Confirm whether treatment is needed',
    sizeMath: 'A starting size for your household. Confirm the final fit with the full calculator.',
    largerMath: 'Your household may need a larger system. Use the full calculator or ask a specialist.',
    largeHouseholdMath: 'Eight or more people needs an exact household count and peak-flow check. Use the full calculator to size your system.',
    lowHardnessMath: 'At this hardness, you may not need a softener. Confirm your water test and the problem you want to solve before buying.',
    invalidMath: 'Enter a valid hardness and household size to estimate capacity.',
  };

  const decide = ({ src, gpg, salt }) => {
    if (!['city', 'well'].includes(src) || !['low', 'mid', 'high', 'unk'].includes(gpg) || !['ok', 'no'].includes(salt)) return null;
    if (gpg === 'high') return { key: 'softener', reason: 'reasonHigh' };
    if (salt === 'no') return { key: 'conditioner', reason: src === 'well' ? 'reasonNoSaltWell' : gpg === 'low' ? 'reasonLow' : gpg === 'unk' ? 'reasonUnknown' : 'reasonNoSalt' };
    if (src === 'well') return { key: 'softener', reason: 'reasonWell' };
    if (gpg === 'mid') return { key: 'softener', reason: 'reasonMid' };
    return { key: 'conditioner', reason: gpg === 'low' ? 'reasonLow' : 'reasonUnknown' };
  };

  const readJSON = (root, selector) => {
    try {
      const value = JSON.parse(root.querySelector(selector)?.textContent || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    }
    catch (_) { return {}; }
  };
  const safeURL = (value, fallback) => {
    if (typeof value !== 'string' || !value || value === '#') return fallback;
    try {
      const url = new URL(value, window.location.href);
      return ['http:', 'https:'].includes(url.protocol) ? value : fallback;
    } catch (_) { return fallback; }
  };
  const validVariant = (value) => value && /^\d+$/.test(String(value.id)) && typeof value.price === 'string';

  const mount = (root) => {
    if (instances.has(root)) return;
    const cleanups = [];
    let destroyed = false;
    const $ = (selector, scope = root) => scope.querySelector(selector);
    const $$ = (selector, scope = root) => [...scope.querySelectorAll(selector)];
    const on = (target, event, listener, options) => {
      if (!target) return;
      target.addEventListener(event, listener, options);
      cleanups.push(() => target.removeEventListener(event, listener, options));
    };
    const text = (selector, value, scope = root) => {
      const node = $(selector, scope);
      if (node) node.textContent = value;
    };
    const focus = (node) => {
      if (!node) return;
      if (!node.hasAttribute('tabindex')) node.tabIndex = -1;
      node.focus({ preventScroll: true });
    };
    const strings = { ...defaults, ...readJSON(root, '[data-sx-strings]') };
    const t = (key, values = {}) => String(strings[key] || defaults[key] || '').replace(/\{(\w+)\}/g, (_, name) => values[name] ?? '');
    const catalog = readJSON(root, '[data-sx-catalog]');
    const contactURL = safeURL(catalog.contactUrl, '/pages/contact-us');
    const sizeURL = safeURL(catalog.sizeCalculatorUrl, '/pages/water-softener-size-calculator');
    const number = (value) => value.toLocaleString(document.documentElement.lang || 'en');
    const pressed = (buttons, active) => buttons.forEach(button => button.setAttribute('aria-pressed', String(button === active)));
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    root.dataset.sxReady = 'true';
    root.classList.add('is-enhanced');

    instances.set(root, () => {
      destroyed = true;
      cleanups.reverse().forEach(cleanup => cleanup());
      delete root.dataset.sxReady;
      root.classList.remove('is-enhanced');
      instances.delete(root);
    });

    const reveal = $$('.sx-reveal');
    if ('IntersectionObserver' in window && !motion.matches) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      }), { threshold: 0.12 });
      reveal.forEach(node => observer.observe(node));
      cleanups.push(() => observer.disconnect());
    } else reveal.forEach(node => node.classList.add('is-in'));

    const stage = $('[data-stage]'), range = $('[data-stage-range]');
    if (stage && range) {
      const phone = window.matchMedia('(max-width: 1099px)');
      let frame = 0, timer = 0, interacted = false, stopped = false, generation = 0;
      const minimum = () => phone.matches ? 0 : 46;
      const rest = () => phone.matches ? 72 : 66;
      const position = (value) => {
        const pos = Math.min(100, Math.max(minimum(), value));
        stage.style.setProperty('--pos', `${pos}%`);
        range.value = String((pos - minimum()) / (100 - minimum()) * 100);
        range.setAttribute('aria-valuetext', t('compareValue', { percent: Math.round(100 - pos) }));
      };
      const cancel = () => {
        stopped = true;
        generation++;
        clearTimeout(timer);
        cancelAnimationFrame(frame);
      };
      const input = () => {
        interacted = true;
        cancel();
        position(minimum() + (100 - minimum()) * Number(range.value) / 100);
      };
      // Mouse users grab the visible handle without snapping it to the pointer.
      // Touch keeps the whole image target so the small grip is not a mobile gate.
      const grip = $('.sx-grip', stage);
      let dragging = null, grabOffset = 0;
      const pointerPosition = (event) => {
        const bounds = range.getBoundingClientRect();
        if (!bounds.width) return;
        const fraction = Math.min(1, Math.max(0, (event.clientX - grabOffset - bounds.left) / bounds.width));
        position(minimum() + (100 - minimum()) * fraction);
      };
      on(stage, 'pointerdown', event => {
        if (event.button !== 0 || dragging !== null) return;
        const bounds = range.getBoundingClientRect();
        if (!bounds.width) return;
        const mouse = event.pointerType === 'mouse';
        if (mouse) {
          const handle = grip?.getBoundingClientRect();
          if (!handle || event.clientX < handle.left || event.clientX > handle.right ||
              event.clientY < handle.top || event.clientY > handle.bottom) return;
        } else if (event.clientX < bounds.left || event.clientX > bounds.right) {
          return;
        }
        interacted = true;
        cancel();
        dragging = event.pointerId;
        grabOffset = mouse ? event.clientX - bounds.left - bounds.width * Number(range.value) / 100 : 0;
        stage.classList.add('is-dragging', 'is-pointer-active');
        event.preventDefault();
        range.focus({ preventScroll: true });
        stage.setPointerCapture?.(event.pointerId);
        if (!mouse) pointerPosition(event);
      });
      on(stage, 'pointermove', event => {
        if (dragging === null || dragging !== event.pointerId) return;
        pointerPosition(event);
      });
      const endDrag = event => {
        if (dragging !== event.pointerId) return;
        dragging = null;
        stage.classList.remove('is-dragging');
        if (stage.hasPointerCapture?.(event.pointerId)) stage.releasePointerCapture(event.pointerId);
      };
      on(stage, 'pointerup', endDrag);
      on(stage, 'pointercancel', endDrag);
      on(stage, 'lostpointercapture', endDrag);
      on(range, 'keydown', () => {
        stage.classList.remove('is-pointer-active');
        interacted = true;
        cancel();
      });
      on(range, 'blur', () => stage.classList.remove('is-pointer-active'));
      on(range, 'input', input);
      on(window, 'resize', () => {
        cancel();
        position(interacted ? minimum() + (100 - minimum()) * Number(range.value) / 100 : rest());
      }, { passive: true });
      on(motion, 'change', () => { cancel(); if (!interacted) position(rest()); });
      const animate = () => {
        if (destroyed || interacted || stopped || motion.matches) return;
        const token = generation;
        timer = setTimeout(() => {
          let start;
          const step = timestamp => {
            if (destroyed || interacted || token !== generation) return;
            if (start === undefined) start = timestamp;
            const progress = Math.min(1, (timestamp - start) / 2400);
            position(100 + (rest() - 100) * (1 - Math.pow(1 - progress, 3)));
            if (progress < 1) frame = requestAnimationFrame(step);
          };
          frame = requestAnimationFrame(step);
        }, 500);
      };
      position(motion.matches ? rest() : 100);
      const image = $('img.is-soft', stage);
      if (image && !image.complete) on(image, 'load', animate, { once: true });
      else animate();
      cleanups.push(cancel);
    }

    $$('[data-sx-product]').forEach(card => {
      const product = catalog[card.dataset.sxProduct] || {};
      const buttons = $$('[data-s]', card);
      const input = $('input[name="id"]', card), configure = $('[data-configure]', card), add = $('[data-add]', card);
      const select = (button) => {
        if ($('product-form', card)?.submitting) return;
        const variant = product.variants?.[button?.dataset.s];
        const valid = validVariant(variant);
        const available = valid && variant.available === true;
        pressed(buttons, button);
        text('[data-price]', valid ? variant.price : t('unavailable'), card);
        if (input) {
          input.value = valid ? String(variant.id) : '';
          input.disabled = !valid;
        }
        const quantity = $('input[name="quantity"]', card);
        if (quantity && valid) quantity.value = String(Math.max(1, Number(variant.quantity) || 1));
        $$('[data-product-link]', card).forEach(link => {
          link.href = safeURL(valid && variant.url, safeURL(product.url, contactURL));
        });
        const image = $('[data-product-image]', card);
        const imageURL = safeURL(valid && variant.img, safeURL(product.img, null));
        if (image && imageURL && image.getAttribute('src') !== imageURL) {
          image.removeAttribute('srcset');
          image.src = imageURL;
        }
        if (configure) {
          const url = safeURL(valid && variant.url, safeURL(product.url, contactURL));
          configure.href = url;
          configure.dataset.productUrl = url;
          configure.toggleAttribute('data-cq-choose-options', valid);
        }
        if (add) {
          add.disabled = !available;
          add.setAttribute('aria-disabled', String(!available));
          const label = $('[data-add-label]', add);
          if (label) label.textContent = available && add.hasAttribute('data-cq-full-page')
            ? add.dataset.cqPageLabel || t('addToCart')
            : t(available ? 'addToCart' : valid ? 'soldOut' : 'unavailable');
        }
      };
      buttons.forEach(button => on(button, 'click', () => select(button)));
      select(buttons.find(button => button.getAttribute('aria-pressed') === 'true') || buttons[0]);
    });

    const moments = $$('[data-moment]'), frames = $$('.sx-day-frame img'), bars = $$('.sx-day-progress i');
    if (moments.length && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        const entry = entries.filter(item => item.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!entry) return;
        const index = moments.indexOf(entry.target);
        moments.forEach((moment, n) => moment.classList.toggle('is-on', n === index));
        frames.forEach((image, n) => image.classList.toggle('is-on', n === index));
        bars.forEach((bar, n) => bar.classList.toggle('is-on', n <= index));
        text('.sx-day-caption [data-moment-caption]', entry.target.dataset.caption || $('h3', entry.target)?.textContent || '');
      }, { rootMargin: '-35% 0px -35% 0px', threshold: 0 });
      moments.forEach(moment => observer.observe(moment));
      cleanups.push(() => observer.disconnect());
    }

    const picker = $('[data-pick]');
    if (picker) {
      const steps = $$('[data-step]', picker), back = $('[data-back]', picker);
      const result = $('[data-pick-a]', picker), resultName = $('[data-p-name]', picker);
      const note = $('[data-pick-note]', picker);
      let summary = $('.sx-pick-summary', picker);
      if (!summary && steps.length) {
        summary = document.createElement('div');
        summary.className = 'sx-pick-summary';
        steps[0].parentElement.appendChild(summary);
      }
      let restart = $('[data-restart]', picker);
      if (!restart && note) {
        restart = document.createElement('button');
        restart.type = 'button';
        restart.className = 'sx-back';
        restart.dataset.restart = '';
        note.after(restart);
      }
      if (restart) restart.textContent = t('startOver');
      let current = 0, state = {}, completed = false;
      const showStep = (index, moveFocus = false) => {
        current = Math.max(0, Math.min(steps.length - 1, index));
        completed = false;
        if (summary) { summary.hidden = true; summary.replaceChildren(); }
        steps.forEach((step, n) => {
          step.hidden = n !== current;
          step.classList.toggle('is-on', n === current);
        });
        $('[data-bar]', picker)?.style.setProperty('--p', `${(current + 1) / steps.length * 100}%`);
        text('[data-step-label]', t('question', { number: current + 1, total: steps.length }), picker);
        if (back) back.hidden = current === 0;
        if (restart) restart.hidden = true;
        if (note) { note.hidden = false; note.textContent = t('timeNote'); }
        if (moveFocus) focus($('h3', steps[current]) || $('button', steps[current]));
      };
      const resetResult = () => {
        // Preserve DOM and existing section-editor attributes rather than replacing the result panel.
        const product = catalog.softener || {};
        text('[data-p-kicker]', t('initialKicker'), picker);
        text('[data-p-name]', product.name || '', picker);
        text('[data-p-why]', t('initialWhy'), picker);
        text('[data-p-price]', product.price || '', picker);
        const limit = $('[data-p-limit]', picker);
        if (limit) { limit.textContent = ''; limit.hidden = true; }
        const cta = $('[data-p-cta]', picker);
        if (cta) { cta.textContent = t('seeSystems'); cta.href = '#systems'; }
        result?.classList.remove('is-blue', 'is-result');
        const image = $('[data-p-img]', picker);
        const initialSource = safeURL(image?.dataset.initialSrc, safeURL(product.img, null));
        if (image && initialSource) {
          image.src = initialSource;
          image.removeAttribute('srcset');
          image.alt = image.dataset.initialAlt ?? product.name ?? '';
        }
      };
      const showResult = () => {
        const recommendation = decide(state);
        if (!recommendation) return;
        const product = catalog[recommendation.key];
        const configured = validVariant(product?.variants?.['1.5']) ? product.variants['1.5'] : null;
        completed = true;
        steps.forEach(step => { step.hidden = true; step.classList.remove('is-on'); });
        text('[data-step-label]', t('ready'), picker);
        $('[data-bar]', picker)?.style.setProperty('--p', '100%');
        if (back) back.hidden = false;
        if (restart) restart.hidden = false;
        if (note) note.hidden = true;
        if (summary) {
          const heading = document.createElement('h3');
          heading.textContent = t('answersHeading');
          const list = document.createElement('dl');
          steps.forEach(step => {
            const group = $('[data-q]', step);
            const selected = group && $$('button[data-v]', group).find(button => button.dataset.v === state[group.dataset.q]);
            if (!selected) return;
            const question = document.createElement('dt');
            question.textContent = $('h3', step)?.textContent || '';
            const answer = document.createElement('dd');
            answer.textContent = [...selected.childNodes].filter(node => node.nodeType === 3).map(node => node.textContent).join('').trim() || selected.textContent;
            list.append(question, answer);
          });
          summary.replaceChildren(heading, list);
          summary.hidden = false;
        }
        text('[data-p-kicker]', t('pickKicker'), picker);
        text('[data-p-name]', product?.name || t('specialist'), picker);
        text('[data-p-why]', t(recommendation.reason), picker);
        text('[data-p-price]', configured?.price || product?.price || '', picker);
        const limit = $('[data-p-limit]', picker);
        if (limit) {
          limit.textContent = t(recommendation.key === 'softener' ? 'softenerLimit' : 'conditionerLimit');
          limit.hidden = false;
        }
        const cta = $('[data-p-cta]', picker);
        if (cta) {
          cta.textContent = t(product ? recommendation.key === 'softener' ? 'seeSoftener' : 'seeConditioner' : 'specialist');
          cta.href = safeURL(configured?.url, safeURL(product?.url, contactURL));
        }
        result?.classList.toggle('is-blue', recommendation.key === 'conditioner');
        result?.classList.add('is-result');
        const image = $('[data-p-img]', picker);
        if (image && product?.img) {
          image.src = product.img;
          image.removeAttribute('srcset');
          image.alt = product.name || '';
        }
        focus(resultName || result);
      };
      $$('[data-q]', picker).forEach((group, index) => {
        $$('button[data-v]', group).forEach(button => on(button, 'click', () => {
          if (completed || index !== current) return;
          pressed($$('button[data-v]', group), button);
          state[group.dataset.q] = button.dataset.v;
          // Synchronous transitions avoid a delayed answer overriding Back or Start over.
          if (index < steps.length - 1) showStep(index + 1, true);
          else showResult();
        }));
      });
      on(back, 'click', () => {
        const target = completed ? steps.length - 1 : current - 1;
        resetResult();
        showStep(target, true);
      });
      on(restart, 'click', () => {
        state = {};
        $$('.sx-answer', picker).forEach(button => button.setAttribute('aria-pressed', 'false'));
        resetResult();
        showStep(0, true);
      });
      if (steps.length) showStep(0);
    }

    const hardness = $('[data-gpg]');
    if (hardness) {
      const peopleButtons = $$('[data-people] button');
      let people = Number(peopleButtons.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.n || 4);
      const render = () => {
        const gpg = Number(hardness.value), gallons = 60 + 30 * people, grains = gallons * gpg;
        const valid = Number.isFinite(gpg) && gpg >= 1 && gpg <= 40 && Number.isInteger(people) && people >= 1 && people <= 8;
        const band = gpg <= 3.5 ? 'softBand' : gpg <= 7 ? 'moderateBand' : gpg <= 10.5 ? 'hardBand' : 'veryHardBand';
        text('[data-people-out]', people >= 8 ? '8+' : number(people));
        text('[data-gpg-out]', valid ? number(gpg) : '');
        text('[data-band]', valid ? t(band) : '');
        hardness.style.setProperty('--fill', `${valid ? (gpg - 1) / 39 * 100 : 0}%`);
        const options = [{ size: '1.5', grains: 48000, usable: 42750 }, { size: '2.0', grains: 60000, usable: 57000 }];
        // Seven operating days plus a reserve day; this is capacity, not valve scheduling or a flow-rate check.
        const fit = valid && people < 8 && gpg > 3.5 ? options.find(option => option.usable >= grains * 8) : null;
        const variant = fit && catalog.softener?.variants?.[fit.size];
        const cta = $('[data-size-cta]');
        if (fit) {
          text('[data-size-name]', t('sizeName', { size: fit.size, grains: number(fit.grains) }));
          text('[data-math]', t('sizeMath'));
          if (cta) {
            cta.textContent = t(validVariant(variant) ? 'shopSize' : 'specialist', { size: fit.size });
            cta.href = validVariant(variant) ? safeURL(variant.url, contactURL) : contactURL;
          }
        } else {
          text('[data-size-name]', t(people >= 8 ? 'checkUse' : valid && gpg <= 3.5 ? 'checkHardness' : 'largerSize'));
          text('[data-math]', t(!valid ? 'invalidMath' : people >= 8 ? 'largeHouseholdMath' : gpg <= 3.5 ? 'lowHardnessMath' : 'largerMath'));
          if (cta) { cta.textContent = t(people >= 8 ? 'fullCalculator' : 'specialist'); cta.href = people >= 8 ? sizeURL : contactURL; }
        }
      };
      peopleButtons.forEach(button => on(button, 'click', () => {
        people = Number(button.dataset.n);
        pressed(peopleButtons, button);
        render();
      }));
      on(hardness, 'input', render);
      render();
    }

    const questions = $$('.sx-faq details');
    questions.forEach(question => on(question, 'toggle', () => {
      if (question.open) questions.forEach(other => { if (other !== question) other.open = false; });
    }));
  };

  const roots = (scope) => [
    ...(scope.matches?.(rootSelector) ? [scope] : []),
    ...scope.querySelectorAll(rootSelector),
  ];
  const init = (scope = document) => roots(scope).forEach(mount);
  const destroy = (scope) => roots(scope).forEach(root => instances.get(root)?.());
  window.CQSoftenersV3 = { init, destroy };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
